import { type FC, useCallback, useEffect, useRef, useState } from 'react';
import EditorToolbar, { type ToolbarOption } from './EditorToolbar';
import { useFlushSyncCallback } from '@editor2/hooks/useFlushSyncCallback';

import { BsCode } from 'react-icons/bs';
import { Controlled as CodeMirror } from 'react-codemirror2';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';

import { useCodeMirrorStrictModeHandlers } from '@editor2/hooks/useCodeMirrorStrictModeHandlers';

import 'codemirror/lib/codemirror.css';
import 'codemirror/mode/xml/xml';

import 'codemirror/addon/lint/lint';
import 'codemirror/addon/lint/lint.css';
import 'codemirror/addon/lint/html-lint';

import 'codemirror/addon/fold/foldcode.js';
import 'codemirror/addon/fold/foldgutter.css';
import 'codemirror/addon/fold/foldgutter.js';
import 'codemirror/addon/fold/xml-fold.js';

import 'codemirror/addon/hint/xml-hint.js';
import 'codemirror/addon/hint/html-hint.js';
import 'codemirror/addon/hint/show-hint.css';
import 'codemirror/addon/hint/show-hint.js';

import { HTMLHint as htmlhint } from 'htmlhint';

import { beautifyHtml } from '@editor2/services/htmlService';
import { textEditor } from './styles';
import { htmlSchema } from './schema';
import { lintRules } from '@editor2/components/CodeMirrorHTML/lintRules';
import ShortcutsInfo from '@editor2/components/ShortcutsInfo';

import { type QuillWrapperProps, QuillWrapper } from './QuillWrapper';
import type Quill from 'quill/core/quill';

declare global {
	interface Window {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		htmlhint: any;
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		clipboardData: any;
	}
}
window.htmlhint = htmlhint;

const codeMirrorOptions = {
	mode: 'text/html',
	lineNumbers: true,
	lineWrapping: true,
	foldGutter: true,
	lint: { options: { rules: lintRules } },
	gutters: ['CodeMirror-lint-markers', 'CodeMirror-linenumbers', 'CodeMirror-foldgutter'],
	extraKeys: {
		Tab: 'false',
		'Shift-Tab': 'false',
		'Shift-Alt-F': 'indentAuto',
		'Shift-Option-F': 'indentAuto',
		'Ctrl-Space': 'autocomplete',
		'Cmd-Space': 'autocomplete'
	},
	hintOptions: { schemaInfo: htmlSchema },
	// Configure history for better undo/redo preservation
	undoDepth: 300,
	historyEventDelay: 2000
};

/** How many recent emissions the echo-detection ring buffer retains. */
const ECHO_BUFFER_SIZE = 20;

interface Props {
	toolbar_id: string;
	allowedOptions?: ToolbarOption[];
	text: string;
	onChange: (value: string) => void;
	forceCodeMode?: boolean;
}

const RichTextEditor: FC<Props> = (props) => {
	const { toolbar_id, allowedOptions, text, onChange, forceCodeMode } = props;

	const [quillHtmlValue, setQuillHtmlValue] = useState(text);
	const [codemirrorHtmlValue, setCodemirrorHtmlValue] = useState(text);

	const [isCodeMode, setCodeMode] = useState(false);
	const [enableQuill, setEnableQuill] = useState(true);

	const quillRef = useRef<Quill | null>(null);
	/*
	 * Ring buffer of recently emitted body values (beautified). The debounced `text`
	 * prop can echo back an *intermediate* value that is no longer the latest emit.
	 * Comparing against only the most recent value would misclassify such a stale
	 * echo as an external update, resyncing the active editor and jumping the cursor
	 * (TECH-2530). Keeping the last several values lets echo detection succeed even
	 * when the user types faster than the Redux debounce interval.
	 *
	 * The buffer is reset whenever a genuine external (non-echo) update arrives,
	 * which limits the window for false-positive collisions. A residual edge case
	 * remains: if the user starts typing immediately after an external update, new
	 * emissions fill the buffer, and a *second* external update then arrives whose
	 * content coincidentally matches one of those intermediate keystrokes, it would
	 * be misclassified as an echo and the resync would be skipped. In practice this
	 * requires two rapid external updates where the second exactly matches a
	 * transient intermediate value — effectively impossible, and the consequences
	 * are benign (the user sees stale content and can simply retype). Eliminating
	 * this entirely would require threading a monotonic sequence number through
	 * Redux alongside `body` so echo detection uses identity rather than content
	 * comparison; we evaluated this and decided the added complexity across three
	 * layers (component → action → reducer) isn't justified by the risk.
	 */
	const emittedRingRef = useRef<string[]>([beautifyHtml(text)]);
	/** Append a beautified value to the ring buffer, evicting the oldest entry.
	 *  The caller should pass the SAME value it passes to `onChange` — not the
	 *  raw editor content — so the ring entry matches what `beautifyHtml(text)`
	 *  will produce when the echo arrives in the text-effect. For the Quill path
	 *  this means passing the already-beautified value (which gets beautified
	 *  again here). That double-beautification is intentional: `onChange` sends
	 *  the beautified value, the text-effect beautifies the incoming `text` prop,
	 *  so the ring must store `beautifyHtml(beautified)` to match. Passing raw
	 *  content instead would break echo detection whenever `beautifyHtml` is not
	 *  perfectly idempotent. */
	const pushEmitted = useCallback((raw: string) => {
		const beautified = beautifyHtml(raw);
		const ring = emittedRingRef.current;
		ring.push(beautified);
		if (ring.length > ECHO_BUFFER_SIZE) {
			ring.splice(0, ring.length - ECHO_BUFFER_SIZE);
		}
	}, []);
	/** Check whether a beautified value matches any recent emission. */
	const isRecentEmit = useCallback((beautified: string) => {
		return emittedRingRef.current.includes(beautified);
	}, []);
	const { wrapperRef, handleEditorDidMount, handleEditorWillUnmount } =
		useCodeMirrorStrictModeHandlers();

	/*
	 * Diagnostic logging for the recurring cursor-jump bug (TECH-2530). These logs are
	 * intentionally compact and structured so they can be reviewed from a FullStory
	 * session or copy/pasted from the DevTools console when the issue is observed. The
	 * `seq` field lets us order events; `toolbar_id` distinguishes concurrent editors.
	 */
	const logSeqRef = useRef(0);
	const log = useCallback(
		// eslint-disable-next-line @typescript-eslint/consistent-indexed-object-style
		(event: string, data?: Record<string, unknown>) => {
			/*
			 * Serialize the payload into the message string so it stays readable when the
			 * console output is saved from Chrome DevTools (object args are otherwise
			 * written out as opaque `Object` entries).
			 */
			const payload = JSON.stringify({ seq: ++logSeqRef.current, ...data });
			// eslint-disable-next-line no-console
			console.log(`[TECH-2530][${toolbar_id}] ${event} ${payload}`);
		},
		[toolbar_id]
	);
	const warn = useCallback(
		// eslint-disable-next-line @typescript-eslint/consistent-indexed-object-style
		(event: string, data?: Record<string, unknown>) => {
			const payload = JSON.stringify({ seq: ++logSeqRef.current, ...data });
			// eslint-disable-next-line no-console
			console.warn(`[TECH-2530][${toolbar_id}] ${event} ${payload}`);
		},
		[toolbar_id]
	);

	// Wrap handleCodemirrorHTMLChange with flushSync for synchronous updates
	const syncHandleCodemirrorHTMLChange = useFlushSyncCallback(
		useCallback(
			(value: string) => {
				const delta = quillRef.current.clipboard.convert({ html: value });
				const selection = quillRef.current.getSelection();
				quillRef.current.setContents(delta);
				if (selection) {
					quillRef.current.setSelection(selection.index, selection.length);
				}
				const editorValue = quillRef.current.root.innerHTML;

				setEnableQuill(value === beautifyHtml(editorValue));

				setCodemirrorHtmlValue(value);
				pushEmitted(value);
				log('emit-codemirror', { emittedStable: value === beautifyHtml(value) });
				onChange(value);
			},
			[onChange, log, pushEmitted]
		)
	);

	const handleQuillHTMLChange: QuillWrapperProps['onChange'] = (
		content,
		_delta,
		source,
		_editor
	) => {
		if (source === 'user') {
			const beautified = beautifyHtml(content);
			setQuillHtmlValue(content);
			pushEmitted(beautified);
			log('emit-quill', { hasFocus: !!quillRef.current?.hasFocus() });
			onChange(beautified);
		}
	};

	useEffect(() => {
		const delta = quillRef.current.clipboard.convert({ html: codemirrorHtmlValue });
		const selection = quillRef.current.getSelection();
		quillRef.current.setContents(delta);
		if (selection) {
			quillRef.current.setSelection(selection.index, selection.length);
		}
		const editorValue = quillRef.current.root.innerHTML;
		setEnableQuill(codemirrorHtmlValue === beautifyHtml(editorValue));
		// Run only once on mount
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		log('force-code-mode', { forceCodeMode });
		setCodeMode(forceCodeMode);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [forceCodeMode]);

	useEffect(() => {
		const incoming = beautifyHtml(text);
		/*
		 * When `text` is just the debounced echo of edits made in the active editor,
		 * re-applying it would rebuild that editor's contents mid-typing and clamp the
		 * caret to the end of the element. So on an echo we sync only the inactive
		 * editor; a genuine external update (text we didn't emit) is applied to both.
		 */
		const isEcho = isRecentEmit(incoming);

		/*
		 * Skip the editor the user is actively typing in when this is its own echo
		 * (Quill is active in WYSIWYG mode, CodeMirror in code mode).
		 */
		const skipQuillSync = isEcho && !isCodeMode;
		const skipCodemirrorSync = isEcho && isCodeMode;

		/*
		 * Echo detection hinges on beautifyHtml being idempotent and stable. If the
		 * incoming value is not stable we may misclassify our own echo as an external
		 * update and resync the active editor, so surface it explicitly.
		 */
		const incomingStable = incoming === beautifyHtml(incoming);
		const quillFocused = !isCodeMode && !!quillRef.current?.hasFocus();

		log('text-effect', {
			isEcho,
			isCodeMode,
			quillFocused,
			skipQuillSync,
			skipCodemirrorSync,
			incomingStable,
			echoBufferSize: emittedRingRef.current.length,
			quillNeedsSync: incoming !== beautifyHtml(quillHtmlValue),
			codemirrorNeedsSync: incoming !== beautifyHtml(codemirrorHtmlValue)
		});

		// beautifyHtml is not idempotent — echo detection may misfire
		if (!incomingStable) {
			warn('beautifyHtml-not-idempotent', { incomingStable });
		}
		// About to resync the active editor while the user has focus — likely cursor jump
		if (quillFocused && !skipQuillSync && incoming !== beautifyHtml(quillHtmlValue)) {
			warn('quill-resync-while-focused', { isEcho });
		}
		if (isCodeMode && !skipCodemirrorSync && incoming !== beautifyHtml(codemirrorHtmlValue)) {
			warn('codemirror-resync-while-active', { isEcho });
		}

		if (incoming !== beautifyHtml(quillHtmlValue) && !skipQuillSync) {
			setQuillHtmlValue(text);

			const delta = quillRef.current.clipboard.convert({ html: text });
			const selection = quillRef.current.getSelection();
			const hadFocus = quillRef.current.hasFocus();
			quillRef.current.setContents(delta);
			if (selection) {
				quillRef.current.setSelection(selection.index, selection.length);
			}
			const editorValue = quillRef.current.root.innerHTML;
			const restoredSelection = quillRef.current.getSelection();
			/*
			 * If Quill had focus and the caret didn't land back where it started, this is
			 * very likely a user-visible cursor jump. `contentShapeChanged` flags when the
			 * rebuilt content differs from the incoming text, which is what causes the
			 * caret to clamp to the end of the element.
			 */
			const caretMoved =
				!!selection &&
				(!restoredSelection ||
					restoredSelection.index !== selection.index ||
					restoredSelection.length !== selection.length);
			log('quill-resync', {
				hadFocus,
				caretMoved,
				contentShapeChanged: text !== beautifyHtml(editorValue),
				selectionBefore: selection,
				selectionAfter: restoredSelection
			});
			if (hadFocus && caretMoved) {
				warn('cursor-jump-detected', {
					selectionBefore: selection,
					selectionAfter: restoredSelection,
					contentShapeChanged: text !== beautifyHtml(editorValue)
				});
			}

			setEnableQuill(text === beautifyHtml(editorValue));
		}

		if (incoming !== beautifyHtml(codemirrorHtmlValue) && !skipCodemirrorSync) {
			log('codemirror-resync', { isCodeMode });
			setCodemirrorHtmlValue(text);
		}

		/*
		 * When the incoming value is NOT an echo, we just accepted an external update
		 * and resynced both editors. All prior emissions are now stale and must not
		 * match future echoes — otherwise a later external update that coincidentally
		 * matches an old intermediate value would be silently swallowed. Seed the
		 * buffer with the accepted value so the immediate echo of this resync is
		 * still recognized.
		 */
		if (!isEcho) {
			emittedRingRef.current = [incoming];
			log('echo-buffer-reset', { reason: 'external-update' });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [text]);

	return (
		<div className={textEditor}>
			<div className="toolbarContainer">
				<div className="toolbar">
					<div
						className="codeMode"
						onClick={() => {
							log('toggle-code-mode', { from: isCodeMode, to: !isCodeMode });
							setCodeMode(!isCodeMode);
						}}>
						<BsCode />
					</div>

					<div className={`${isCodeMode ? 'hidden' : ''} ${enableQuill ? '' : 'toolbarDisabled'}`}>
						<EditorToolbar toolbar_id={toolbar_id} allowedOptions={allowedOptions} />
					</div>

					{isCodeMode && <ShortcutsInfo />}
				</div>

				{!enableQuill && !isCodeMode && (
					<OverlayTrigger
						overlay={
							<Tooltip id="tooltip-disabled">
								The HTML contains tags/attributes that may be displayed incorrectly.
							</Tooltip>
						}>
						<span className="info">READONLY</span>
					</OverlayTrigger>
				)}
			</div>

			{isCodeMode && (
				<CodeMirror
					ref={wrapperRef}
					value={codemirrorHtmlValue}
					options={codeMirrorOptions}
					onBeforeChange={(_editor, _data, value) => syncHandleCodemirrorHTMLChange(value)}
					editorDidMount={handleEditorDidMount}
					editorWillUnmount={handleEditorWillUnmount}
				/>
			)}

			<div className={isCodeMode ? 'hidden' : ''}>
				<QuillWrapper
					ref={quillRef}
					onChange={handleQuillHTMLChange}
					readOnly={!enableQuill}
					toolbarId={toolbar_id}
				/>
			</div>
		</div>
	);
};

export default RichTextEditor;
